import { useEffect, useState } from "react";
import { CategoryOrder } from "./CategoryOrder";
import * as Icons from "lucide-react";
import { api, errText } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { CATEGORIES } from "../../lib/constants";
import { tileTheme, spreadHues, TILE_PRESETS } from "../../lib/tileColor";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Slider } from "../../components/ui/slider";
import { SeasonalTileSchedules } from "./SeasonalTileSchedules";
import { TilePhotoControl } from "./TilePhotoControl";
import { tilePhoto } from "../../lib/categoryPhotos";
import { CategoryPhoto } from "../../components/CategoryPhoto";
import { toast } from "sonner";
import { Loader2, LayoutGrid, RotateCcw } from "lucide-react";

const TILES = CATEGORIES.filter((c) => c.id !== "all");
const ICON_CHOICES = ["Dog", "Cat", "Bird", "Turtle", "Fish", "Rabbit", "ShoppingBag", "PawPrint", "Bone", "Heart", "Rat", "Squirrel", "Shell", "Egg", "Sparkles", "Home"];

const TileColor = ({ c, ov, onChange }) => {
  const Icon = Icons[ov.icon] || Icons[c.icon] || Icons.PawPrint;
  const th = tileTheme(ov.color);
  const [hex, setHex] = useState(ov.color || "");
  useEffect(() => setHex(ov.color || ""), [ov.color]);
  return (
    <div className="flex items-center gap-2 mb-2" data-testid={`cat-color-row-${c.id}`}>
      <div style={th?.style} data-testid={`cat-color-preview-${c.id}`}
        className={`h-16 w-20 shrink-0 rounded-lg border flex flex-col items-center justify-center gap-1 ${th ? "border-transparent shadow" : "bg-white border-slate-200"}`}>
        {tilePhoto(c.id, ov) ? <CategoryPhoto src={tilePhoto(c.id, ov)} pos={ov.photo_pos} zoom={ov.photo_zoom} className="h-7 w-7" />
          : <span className={`h-6 w-6 rounded-md flex items-center justify-center ${th ? th.iconBg : "bg-[#FFF4F0]"}`}><Icon size={13} className={th ? th.icon : "text-[#F5551E]"} /></span>}
        <span className={`text-[9px] font-semibold leading-tight text-center px-1 ${th ? th.label : "text-[#12234A]"}`}>{ov.label || c.label}</span>
      </div>
      <label className="relative h-9 w-11 rounded-lg border border-slate-200 overflow-hidden cursor-pointer shrink-0" title="Pick tile colour">
        <span className="absolute inset-0" style={{ background: ov.color || "conic-gradient(#F5551E,#E11D74,#7C3AED,#2563EB,#059669,#D97706,#F5551E)" }} />
        <input type="color" value={ov.color || "#F5551E"} onChange={(e) => onChange(e.target.value)} data-testid={`cat-color-${c.id}`} className="absolute inset-0 opacity-0 cursor-pointer" />
      </label>
      <Input value={hex} onChange={(e) => { setHex(e.target.value); if (/^#[0-9a-f]{6}$/i.test(e.target.value)) onChange(e.target.value); }}
        placeholder="#FFFFFF" maxLength={7} data-testid={`cat-color-hex-${c.id}`} className="h-9 rounded-lg text-xs font-mono w-24" />
      {ov.color && (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange("")} data-testid={`cat-color-reset-${c.id}`} className="h-9 rounded-lg text-xs gap-1"><RotateCcw size={12} /> White</Button>
      )}
    </div>
  );
};

const Presets = ({ apply }) => {
  const [start, setStart] = useState("#F5551E");
  const wheel = spreadHues(start, TILES.length);
  const chip = "inline-flex items-center gap-2 h-9 pl-2 pr-3 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-[#12234A] hover:border-[#F5551E] transition-colors";
  const dots = (cols) => <span className="flex -space-x-1">{cols.slice(0, 5).map((c, i) => <span key={i} className="h-4 w-4 rounded-full border border-white" style={{ background: c }} />)}</span>;
  return (
    <div className="rounded-xl bg-slate-50 p-3 mb-4" data-testid="cat-presets">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Colour presets</p>
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(TILE_PRESETS).map(([name, cols]) => (
          <button key={name} type="button" onClick={() => apply(cols)} className={chip} data-testid={`cat-preset-${name.toLowerCase().replace(/ /g, "-")}`}>{dots(cols)} {name}</button>
        ))}
        <button type="button" onClick={() => apply(TILES.map(() => ""))} className={chip} data-testid="cat-preset-white">{dots(["#FFFFFF", "#F8FAFC", "#FFFFFF"])} All white</button>
        <span className="h-6 w-px bg-slate-200 mx-1" />
        <label className="relative h-9 w-9 rounded-full overflow-hidden cursor-pointer border border-slate-200" title="Pick a start colour for the wheel"
          style={{ background: "conic-gradient(#EF4444,#F59E0B,#84CC16,#10B981,#06B6D4,#3B82F6,#A855F7,#EC4899,#EF4444)" }}>
          <span className="absolute inset-2.5 rounded-full border-2 border-white" style={{ background: start }} />
          <input type="color" value={start} onChange={(e) => setStart(e.target.value)} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="cat-wheel-start" />
        </label>
        <button type="button" onClick={() => apply(wheel)} className={chip} data-testid="cat-preset-wheel">{dots(wheel)} Spread around wheel</button>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">Presets fill every tile below. Fine-tune any tile, then press Save tiles.</p>
    </div>
  );
};

export function CategorySettings() {
  const [overrides, setOverrides] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [browseColors, setBrowseColors] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [savedIds, setSavedIds] = useState([]);
  const [holidayOn, setHolidayOn] = useState(true);
  const [speed, setSpeed] = useState(3);
  const [order, setOrder] = useState([]);
  const [hidden, setHidden] = useState([]);
  const [saving, setSaving] = useState(false);
  const loadAll = (d) => { setOverrides(d.overrides || {}); setSchedules(d.schedules || []); setBrowseColors(!!d.browse_colors); setActiveId(d.active_schedule || ""); setSavedIds((d.schedules || []).map((x) => x.id)); setHolidayOn(d.holiday_themes !== false); setSpeed(d.carousel_speed || 3); setOrder(d.order || []); setHidden(d.hidden || []); };

  useEffect(() => {
    api.get("/admin/site/categories").then((r) => loadAll(r.data)).catch(() => setOverrides({}));
  }, []);

  const setField = (id, key, val) =>
    setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), [key]: val } }));

  const applyColors = (cols) =>
    setOverrides((o) => Object.fromEntries([...Object.entries(o), ...TILES.map((c, i) => [c.id, { ...(o[c.id] || {}), color: cols[i % cols.length] || "" }])]));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site/categories", { overrides, schedules, browse_colors: browseColors, holiday_themes: holidayOn, carousel_speed: speed, order, hidden, ...extra });
      loadAll(data);
      toast.success("holiday_themes" in extra ? `Holiday themes ${extra.holiday_themes ? "on" : "paused"}` : "Category tiles updated");
    } catch (e) { toast.error(errText(e)); }
    finally { setSaving(false); }
  };


  if (!overrides) return <div className="h-32 flex items-center justify-center"><Loader2 className="animate-spin text-[#F5551E]" /></div>;

  return (
    <div className="max-w-5xl bg-white rounded-2xl border border-slate-200 p-5 mt-6" data-testid="category-settings">
      <div className="flex items-center gap-2">
        <LayoutGrid size={18} className="text-[#F5551E]" />
        <h3 className="font-display font-bold text-[#12234A]">Homepage category tiles</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1 mb-4">Rename a category tile, add a short description, pick an icon or a tile colour. Leave blank to use the defaults (white tile).</p>

      <Presets apply={applyColors} />
      <CategoryOrder tiles={TILES} order={order} setOrder={setOrder} hidden={hidden} setHidden={setHidden} labels={Object.fromEntries(TILES.map((c) => [c.id, overrides[c.id]?.label || c.label]))} />
      <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-3 mb-4 cursor-pointer" data-testid="browse-colors-row">
        <Switch checked={browseColors} onCheckedChange={setBrowseColors} data-testid="browse-colors-toggle" />
        <span>
          <span className="block text-sm font-semibold text-[#12234A]">Use tile colours on Browse pages</span>
          <span className="block text-xs text-slate-500">Colours each category's Browse header strip and selected category chip (including seasonal colours while a schedule is live).</span>
        </span>
      </label>
      <div className="grid sm:grid-cols-2 gap-3">
        {TILES.map((c) => {
          const Icon = Icons[c.icon] || Icons.PawPrint;
          const ov = overrides[c.id] || {};
          return (
            <div key={c.id} className="rounded-xl border border-slate-100 p-3" data-testid={`cat-edit-${c.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="h-7 w-7 rounded-lg bg-[#FFF4F0] flex items-center justify-center"><Icon size={15} className="text-[#F5551E]" /></span>
                <span className="text-xs font-semibold text-slate-400">Default: {c.label}</span>
              </div>
              <TileColor c={c} ov={ov} onChange={(v) => setField(c.id, "color", v)} />
              <TilePhotoControl c={c} ov={ov} setField={setField} />
              <div className="h-2" />
              <Input value={ov.label || ""} onChange={(e) => setField(c.id, "label", e.target.value)} data-testid={`cat-label-${c.id}`} placeholder={c.label} className="h-9 rounded-lg" />
              <Input value={ov.description || ""} onChange={(e) => setField(c.id, "description", e.target.value)} data-testid={`cat-desc-${c.id}`} placeholder="Short description (optional)" className="h-9 rounded-lg mt-2 text-xs" />
              <Select value={ov.icon || c.icon} onValueChange={(v) => setField(c.id, "icon", v)}>
                <SelectTrigger data-testid={`cat-icon-${c.id}`} className="h-9 rounded-lg mt-2 text-xs"><SelectValue placeholder="Icon" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {(ICON_CHOICES.includes(c.icon) ? ICON_CHOICES : [c.icon, ...ICON_CHOICES]).map((name) => {
                    const IconC = Icons[name] || Icons.PawPrint;
                    return (
                      <SelectItem key={name} value={name}>
                        <span className="flex items-center gap-2"><IconC size={15} className="text-[#F5551E]" /> {name}</span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      <SeasonalTileSchedules schedules={schedules} setSchedules={setSchedules} activeId={activeId} savedIds={savedIds} enabled={holidayOn} setEnabled={(v) => { setHolidayOn(v); save({ holiday_themes: v }); }}
        current={Object.fromEntries(TILES.map((c) => [c.id, overrides[c.id]?.color || "#FFFFFF"]))} />

      <div className="mt-4 rounded-xl border border-slate-200 p-3 flex items-center gap-4 flex-wrap" data-testid="cat-carousel-speed">
        <div className="min-w-[180px]"><Label className="m-0">Carousel speed</Label><p className="text-[11px] text-slate-400">Moves one category every {speed} second{speed === 1 ? "" : "s"} · pauses on hover</p></div>
        <Slider value={[speed]} min={1} max={15} step={1} onValueChange={([v]) => setSpeed(v)} className="flex-1 min-w-[200px]" data-testid="cat-carousel-speed-slider" />
        <span className="text-sm font-semibold text-[#12234A] w-12 text-right" data-testid="cat-carousel-speed-value">{speed}s</span>
      </div>

      <div className="flex gap-2 mt-4">
        <Button onClick={() => save()} disabled={saving} data-testid="cat-save-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
          {saving ? <Loader2 size={16} className="animate-spin" /> : "Save tiles"}
        </Button>
        <Button variant="outline" onClick={() => setOverrides({})} data-testid="cat-reset-btn" className="rounded-xl gap-1.5"><RotateCcw size={15} /> Clear all</Button>
      </div>
    </div>
  );
}
